import { PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects'
import apis from '../apis'
import { actions as appActions } from '../state'
import { ReturnCall } from '../types'
import { defaultFCValue } from './pages/ProductPage/ProductPage.data'
import StockManagementApi from './StockManagementApi'
import {
  selectProductTabFilters,
  selectProductTabsModelId,
  selectProductBarcodesLength,
  selectProductMovementPage,
} from './StockManagementSeletor'
import { actions } from './StockManagementState'
import {
  EditBarcode, FlvProductUpdateAvailabilityPayload, GetFlvProductInfoForReleasePayload, GetInvoiceToPrintPayload, GetProduct, InsertBarcodeParams, UpOrDownOrderBarcode,
} from './StockManagementTypes'
import { InvoiceTypes } from 'src/components/Danfe/danfeTypes'
import { getDanfe } from 'src/components/Danfe/danfeApi'

export function* getProduct(data: PayloadAction<GetProduct>) {
  yield put(appActions.isLoading(true))
  const { fulfillmentCenterId: fcId, modelId } = data.payload

  try {
    const product = yield call(
      StockManagementApi.getProduct,
      {
        fulfillmentCenterId: fcId === defaultFCValue ? undefined : fcId,
        modelId,
      },
    )
    yield put(actions.setProduct(product))
  } catch (error: any) {
    toast.error(`Ocorreu um erro: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getProductBarcodes() {
  const currentBarcodesLength = yield select(selectProductBarcodesLength)
  if (currentBarcodesLength > 0) {
    return
  }

  const modelId = yield select(selectProductTabsModelId)

  yield put(appActions.isLoading(true))
  try {
    const result = yield call(
      StockManagementApi.getProductBarcodes,
      {
        modelId,
      },
    )

    yield put(actions.setProductBarcodes(result.data))
  } catch (error: any) {
    toast.error(`Falha ao carregar barcodes: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getProductMovements() {
  yield put(appActions.isLoading(true))

  try {
    const page = yield select(selectProductMovementPage)
    const {
      fulfillmentCenterId, modelId, startDate, endDate,
    } = yield select(selectProductTabFilters)

    const { data: items, totalItems } = yield call(
      apis.getStockMovements,
      {
        startDate,
        endDate,
        fulfillmentCenterId,
        modelId,
        page,
        pageSize: 100,
      },
    )

    yield put(actions.setProductMovements({ items, totalItems }))
  } catch (error: any) {
    toast.error(`Ocorreu um erro: ${error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getProductReserves() {
  yield put(appActions.isLoading(true))

  try {
    const {
      fulfillmentCenterId, modelId, startDate, endDate,
    } = yield select(selectProductTabFilters)

    const { data: items, totalItems } = yield call(
      StockManagementApi.getProductReserves,
      {
        fulfillmentCenterId,
        modelId,
        startDate,
        endDate,
      },
    )

    yield put(actions.setProductReserves({ items, totalItems }))
  } catch (error: any) {
    toast.error(`Falha ao carregar barcodes: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getProductMovementsByType() {
  yield put(appActions.isLoading(true))

  try {
    const {
      fulfillmentCenterId, modelId, startDate, endDate,
    } = yield select(selectProductTabFilters)

    const { data: items } = yield call(
      StockManagementApi.getProductMovementsByType,
      {
        fulfillmentCenterId,
        modelId,
        startDate,
        endDate,
      },
    )

    yield put(actions.setProductMovementsByType({ items }))
  } catch (error: any) {
    toast.error(`Falha ao carregar histórico: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* deleteBarcode(data: PayloadAction<EditBarcode>) {
  yield put(appActions.isLoading(true))

  try {
    const { modelId, barcode, badge } = data.payload

    const result: ReturnCall<typeof StockManagementApi.deleteBarcode> = yield call(
      StockManagementApi.deleteBarcode,
      { modelId, barcode, badge },
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao deletar Código de Barras!')
      return
    }

    yield put(actions.setProductBarcodes(result.data))
    toast.success('Código de Barras deletado com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao deletar Código de Barras!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* insertNewBarcode({ payload }: PayloadAction<InsertBarcodeParams>) {
  yield put(appActions.isLoading(true))

  try {
    const { onSuccess, ...params } = payload

    const result: ReturnCall<typeof StockManagementApi.insertNewBarcode> = yield call(StockManagementApi.insertNewBarcode, params)

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao inserir novo Código de Barras!')
      return
    }

    const newBarcode = {
      barcode: params.barcode,
      barcodeOrder: result.data,
      modelId: params.modelId,
      type: params.packQty ? 'DUN' : 'EAN',
      packQty: params.packQty || 1,
    }

    yield put(actions.addBarcode(newBarcode))
    onSuccess?.()
    toast.success('Código de Barras inserido com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao inserir novo Código de Barras!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* upBarcodeOrder({ payload }: PayloadAction<UpOrDownOrderBarcode>) {
  yield put(appActions.isLoading(true))

  try {
    const { ...params } = payload
    const result: ReturnCall<typeof StockManagementApi.upBarcodeOrder> = yield call(StockManagementApi.upBarcodeOrder, params)

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao alterar ordem do Código de Barras!')
      return
    }

    yield put(actions.setProductBarcodes(result.data))
    toast.success('Ordem do Código de Barras alterada com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao alterar ordem do Código de Barras!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* downBarcodeOrder({ payload }: PayloadAction<UpOrDownOrderBarcode>) {
  yield put(appActions.isLoading(true))

  try {
    const { ...params } = payload
    const result: ReturnCall<typeof StockManagementApi.downBarcodeOrder> = yield call(StockManagementApi.downBarcodeOrder, params)

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao alterar ordem do Código de Barras!')
      return
    }

    yield put(actions.setProductBarcodes(result.data))
    toast.success('Ordem do Código de Barras alterada com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao alterar ordem do Código de Barras!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getFlvProductInfoForRelease(
  data: PayloadAction<GetFlvProductInfoForReleasePayload>,
) {
  yield put(appActions.isLoading(true))
  yield put(actions.clearProperty(['flvProductInfoForRelease', 'selectedFlvProducts', 'flvRuleDayPeriod']))

  try {
    const result: ReturnCall<typeof StockManagementApi.getFlvProductInfoForRelease> = yield call(
      StockManagementApi.getFlvProductInfoForRelease,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível buscar os produtos FLV')
      return
    }

    const flvProductInfoForRelease = result.data.flvProductInfo.map((product, index) => ({
      ...product,
      id: index,
    }))

    yield put(actions.setProperty({ property: 'flvRuleDayPeriod', value: result.data.dayPeriod }))
    yield put(actions.setProperty({ property: 'flvProductInfoForRelease', value: flvProductInfoForRelease }))
  } catch (error) {
    toast.error('Não foi possível buscar os produtos FLV!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* resendUpdateForFlvRelease(
  data: PayloadAction<FlvProductUpdateAvailabilityPayload>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof StockManagementApi.resendUpdateForFlvRelease> = yield call(
      StockManagementApi.resendUpdateForFlvRelease,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível reenviar atualização para liberação FLV!')
      return
    }

    toast.success('Atualização reenviada com sucesso!')
  } catch (error) {
    toast.error('Não foi possível reenviar atualização para liberação FLV!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getInvoiceToPrint(data: PayloadAction<GetInvoiceToPrintPayload>) {
  yield put(appActions.isLoading(true))

  const { invoiceKey, onSuccess } = data.payload

  try {
    if (!invoiceKey) {
      return
    }

    const result = yield call(getDanfe, {
      invoiceKey,
      type: InvoiceTypes.RECEIVEMENT,
    })

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível buscar as informações da nota fiscal')
      return
    }

    onSuccess(result.data)
  } catch (error: any) {
    toast.error('Não foi possível buscar as informações da nota fiscal!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getProduct.type, getProduct),
    takeLatest(actions.getProductBarcodes.type, getProductBarcodes),
    takeLatest(actions.getProductMovements.type, getProductMovements),
    takeLatest(actions.getProductMovementsByType, getProductMovementsByType),
    takeLatest(actions.getProductReserves.type, getProductReserves),
    takeLatest(actions.deleteBarcode.type, deleteBarcode),
    takeLatest(actions.insertNewBarcode.type, insertNewBarcode),
    takeLatest(actions.upBarcodeOrder.type, upBarcodeOrder),
    takeLatest(actions.downBarcodeOrder.type, downBarcodeOrder),
    takeLatest(actions.getFlvProductInfoForRelease.type, getFlvProductInfoForRelease),
    takeLatest(actions.resendUpdateForFlvRelease.type, resendUpdateForFlvRelease),
    takeLatest(actions.getInvoiceToPrint.type, getInvoiceToPrint),
  ])
}
